import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { MatCheckbox } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbar } from '@angular/material/toolbar';

import { catchError, of, takeUntil, tap, Subject } from 'rxjs';

import { FiltersComponent } from './filters/filters.component';
import { OfficineSelectorComponent } from '../shared/components/officine-selector/officine-selector.component';
import { PromotionsService } from '../shared/services/promotions.service';
import { Promotions } from '../shared/interfaces/promotions/promotions';
import { PromotionStatutChipComponent } from '../shared/components/promotion-statut-chip/promotion-statut-chip.component';
import { Router } from '@angular/router';
import { PromotionDetailsService } from './services/promotion-details-service';
import { CustomNumberFormatPipe } from '../shared/pipe/custom-number-format.pipe';

@Component({
  selector: 'app-promotions',
  standalone: true,
  imports: [
    DatePipe,
    FormsModule,
    MatCheckbox,
    MatIcon,
    MatSortModule,
    MatTableModule,
    MatToolbar,
    FiltersComponent,
    OfficineSelectorComponent,
    PromotionStatutChipComponent,
    CustomNumberFormatPipe,
  ],
  templateUrl: './promotions.component.html',
  styleUrl: './promotions.component.scss',
})
//#region Doc
/**
 * @class PromotionsComponent
 * @implements OnInit, AfterViewInit
 *
 * @description
 * The PromotionsComponent is responsible for displaying and managing a list of promotions.
 * It interacts with the PromotionsService to fetch and display promotions based on the adherent ID.
 * The component also handles sorting and selection of promotions.
 *
 * @property {Subject<void>} _destroying$ - A subject used to manage the unsubscription of observables.
 * @property {string[]} displayedColumns - An array of column names to be displayed in the table.
 * @property {MatSort} sort - A reference to the MatSort directive for sorting the table.
 * @property {MatTableDataSource<Promotions>} dataSource - The data source for the table containing promotions.
 *
 * @constructor
 * @param {PromotionsService} promotionsService - The service used to fetch promotions data.
 *
 * @method ngOnInit
 * Initializes the component and subscribes to the adherent ID observable to fetch promotions.
 *
 * @method ngAfterViewInit
 * Sets up the sorting for the data source after the view has been initialized.
 *
 * @method ngOnDestroy
 * Completes the _destroying$ subject to clean up subscriptions.
 *
 * @method onAdherentChange
 * Fetches promotions based on the adherent ID and updates the data source.
 *
 * @method openDetails
 * Opens a dialog with the details of the selected promotion.
 *
 * @method onSelectionChange
 * Updates the selection state of a promotion.
 *
 * @method updateAllSelected
 * Updates the allSelected property based on the selection state of all promotions.
 *
 * @method setAllSelected
 * Sets the selection state of all promotions to the provided value.
 *
 * @method getColorClass
 * Returns a CSS class based on the selection state of a promotion.
 *
 * @example
 * <app-promotions></app-promotions>
 */
//#endregion
export class PromotionsComponent implements OnInit, AfterViewInit {
  private readonly _destroying$ = new Subject<void>();
  private readonly SORT_STORAGE_KEY = 'promo-table-sort';

  //#region Table
  displayedColumns: string[] = [
    'select',
    'laboratoire',
    'libellePromo',
    'offre',
    'periode',
    'modeCompensation',
    'conditionSellIn',
    'statut',
    'quantiteVendue',
    'totalCompensationCalculee',
    'actions',
  ];
  @ViewChild(MatSort) sort!: MatSort;
  dataSource: MatTableDataSource<Promotions>;
  //#endregion
  allSelected = false;

  constructor(
    private readonly promotionsService: PromotionsService,
    private readonly promotionDetailsService: PromotionDetailsService,
    private readonly router: Router,
  ) {
    this.dataSource = new MatTableDataSource<Promotions>([]);
  }

  ngOnInit(): void {
    this.promotionsService.idAdherent
      .pipe(
        takeUntil(this._destroying$),
        tap(idAdherent => {
          this.onAdherentChange(idAdherent);
        }),
      )
      .subscribe();
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.updateSort();

    const savedSort = localStorage.getItem(this.SORT_STORAGE_KEY);
    if (savedSort) {
      const { active, direction } = JSON.parse(savedSort);

      if (direction != '') {
        this.sort.sort({
          id: active,
          start: direction,
          disableClear: false,
        });
      }
    }

    this.sort.sortChange.subscribe(sort => {
      if (sort.direction === '') {
        localStorage.removeItem(this.SORT_STORAGE_KEY);
      } else {
        localStorage.setItem(this.SORT_STORAGE_KEY, JSON.stringify(sort));
      }
    });
  }

  updateSort() {
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (item: Promotions, property: string) => {
      if (property === 'periode') {
        return new Date(item.dateDebut).getTime();
      }
      if (property === 'libellePromo') {
        return item.libellePromo ?? '';
      }
      if (property === 'statut') {
        return item.statut ?? '';
      }
      return '';
    };
  }

  updateDataSource() {
    this.dataSource = new MatTableDataSource<Promotions>(this.dataSource.data);
    this.updateSort();
  }

  ngOnDestroy() {
    this._destroying$.next();
    this._destroying$.complete();
  }

  setFilterData(dataFromFilter: Promotions[]) {
    this.dataSource.filteredData = dataFromFilter;
    this.updateAllSelected();
  }

  onAdherentChange(idAdherent: number) {
    if (idAdherent == 0) {
      return;
    }
    this.promotionsService
      .getPromotionsByAdherentId(idAdherent)
      .pipe(
        takeUntil(this._destroying$),
        tap(promotions => {
          this.dataSource = new MatTableDataSource<Promotions>(promotions);

          this.updateSort();

          this.updateAllSelected();
        }),
        catchError(() => {
          return of([]);
        }),
      )
      .subscribe();
  }

  openDetails(promotion: Promotions) {
    this.promotionDetailsService.setPromotionDetails(promotion);
    this.router.navigate(['/promotion/details']);
  }

  //#region Selection
  /**
   * Handles the selection change event for a promotion.
   *
   * @param promotion - The promotion object containing the selection details.
   *
   * This method updates the selection status of the given promotion by calling the
   * `putSelectionPromotion` method of the `promotionsService`. It also ensures that
   * any ongoing operations are properly cleaned up using `takeUntil` with the
   * `_destroying$` observable. In case of an error, it catches the error and returns
   * an empty observable. Finally, it calls `updateAllSelected` to refresh the selection
   * state of all promotions.
   */
  onSelectionChange(promotion: Promotions) {
    this.promotionsService
      .putSelectionPromotion(promotion.id, promotion.flagSelection)
      .pipe(
        takeUntil(this._destroying$),
        catchError(() => {
          return of([]);
        }),
      )
      .subscribe();
    this.updateDataSource();
    this.updateAllSelected();
  }

  /**
   * Updates the `allSelected` property based on the selection status of all promotions.
   *
   * If there are no promotions in the data source, `allSelected` is set to `false`.
   * Otherwise, `allSelected` is set to `true` only if all promotions have their `flagSelection` property set to `true`.
   */
  updateAllSelected() {
    if (this.dataSource.filteredData.length === 0) {
      this.allSelected = false;
      return;
    }
    this.allSelected = this.dataSource.filteredData.every(promotion => promotion.flagSelection);
  }

  /**
   * Sets the selection state for all promotions in the data source.
   *
   * @param selected - A boolean indicating whether all promotions should be selected or deselected.
   *
   * This method updates the `allSelected` property and, if there are promotions in the data source,
   * it sends the selection state to the `promotionsService` to update the backend. It also updates
   * the `flagSelection` property for each promotion in the data source.
   *
   * The method handles any errors from the service call by catching them and returning an empty array.
   */
  setAllSelected(selected: boolean) {
    if (this.dataSource.filteredData.length === 0) {
      return;
    }
    this.allSelected = selected;
    this.promotionsService
      .putSelectionsPromotionsList(
        this.dataSource.filteredData.map(promotion => promotion.id),
        selected,
      )
      .pipe(
        takeUntil(this._destroying$),
        tap(() => {
          this.dataSource.filteredData.forEach(promotion => {
            promotion.flagSelection = selected;
          });
          this.updateDataSource();
        }),
        catchError(() => {
          return of([]);
        }),
      )
      .subscribe();
  }
  //#endregion

  getColorClass(promotion: any) {
    if (promotion.flagSelection) {
      return {
        green: true,
      };
    }
    return {};
  }
}
